import { useState, useEffect, useMemo, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ContractInsightsData } from '@/pages/ContractDetail';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { Badge } from '@/components/ui/badge';
import { Search, Check, AlertTriangle, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { format, startOfWeek, addWeeks, addDays, isBefore, isAfter, isSameWeek, isMonday, isSameDay, parseISO } from 'date-fns';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import { useOverdueInterest } from '@/hooks/useOverdueInterest';
import { vehicleRepayment } from '@/lib/vehicle-repayment';

type ViewMode = 'all' | 'partner' | 'management';

interface Props {
    data: ContractInsightsData;
}

const PAGE_SIZE = 15;

interface RepaymentRecord {
    id: number;
    contract_id: number;
    amount: number;
    description: string | null;
    transaction_at: string;
    driver_name: string;
    note: string | null;
    total_count: number;
}

interface PaymentWeek {
    weekStart: Date;
    weekEnd: Date;
    expected: number;
    contractExpected: number;
    payments: RepaymentRecord[];
    totalPaid: number;
    status: 'paid' | 'delayed' | 'partial' | 'missed' | 'upcoming' | 'pending';
    delayDays: number | null;
    note: string | null;
    contractAllocation: number;
}


const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};



export default function ContractInsightVehicle({ data }: Props) {
    const [activeView] = useState<ViewMode>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
    const debouncedSearch = useDebounce(searchTerm, 300);

    const toggleWeekExpanded = (weekKey: string) => {
        setExpandedWeeks(prev => {
            const next = new Set(prev);
            if (next.has(weekKey)) {
                next.delete(weekKey);
            } else {
                next.add(weekKey);
            }
            return next;
        });
    };

    const contractId = data.contract?.id;

    // Reset to page 1 when search changes
    useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);

    // Fetch ALL repayments for this contract (no pagination - we need all for schedule generation)
    const { data: allRepayments, isLoading } = useQuery({
        queryKey: ['contract-all-repayments', contractId],
        queryFn: async () => {
            if (!contractId) return [];

            const { data, error } = await supabase.rpc('get_contract_repayments', {
                p_contract_id: contractId,
                p_from_date: null,
                p_to_date: null,
                p_search: null,
                p_page: 1,
                p_page_size: 1000  // Get all repayments
            });

            if (error) throw error;
            return (data || []) as RepaymentRecord[];
        },
        enabled: !!contractId,
    });


    // Generate payment schedule with expected weeks - Vehicle tab focuses only on contract instalments
    const paymentSchedule = useMemo(() => {
        if (!data.instalments?.length || !allRepayments) return [];

        // Sort instalments by starts_at (ascending)
        const sortedInstalments = [...data.instalments].sort(
            (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()
        );

        // Get contract start date from first instalment
        const contractStart = new Date(sortedInstalments[0].starts_at);
        const today = new Date();

        // Determine the schedule end date based on contract status
        // For COMPLETED contracts, stop at the last payment date
        // For active contracts, continue to today
        let scheduleEnd = today;
        if (data.current_status === 'COMPLETED' && allRepayments.length > 0) {
            const lastPaymentDate = allRepayments.reduce((latest, r) => {
                const paymentDate = new Date(r.transaction_at);
                return paymentDate > latest ? paymentDate : latest;
            }, new Date(allRepayments[0].transaction_at));
            scheduleEnd = lastPaymentDate;
        }

        // Generate all expected weeks from contract start to schedule end
        const weeks: PaymentWeek[] = [];
        let currentWeekStart = startOfWeek(contractStart, { weekStartsOn: 1 }); // Monday
        let instalmentIndex = 0;

        while (isBefore(currentWeekStart, scheduleEnd) || isSameWeek(currentWeekStart, scheduleEnd, { weekStartsOn: 1 })) {
            // Week ends on Sunday (6 days after Monday start)
            const weekEnd = addDays(currentWeekStart, 6);
            // Use next Monday as exclusive boundary for filtering payments
            const nextWeekStart = addWeeks(currentWeekStart, 1);

            // Find applicable contract instalment rate for this week
            while (
                instalmentIndex < sortedInstalments.length - 1 &&
                new Date(sortedInstalments[instalmentIndex + 1].starts_at) <= currentWeekStart
            ) {
                instalmentIndex++;
            }

            const contractInstalment = sortedInstalments[instalmentIndex]?.amount || 0;

            // For Vehicle tab, expected is only the contract instalment (no loans)
            const expectedAmount = contractInstalment;

            // Find payments that fall within this week (Monday to Sunday inclusive)
            const weekPayments = allRepayments.filter(r => {
                const paymentDate = new Date(r.transaction_at);
                return paymentDate >= currentWeekStart && paymentDate < nextWeekStart;
            });

            const totalPaid = weekPayments.reduce((sum, p) => sum + p.amount, 0);

            // Calculate contract allocation from the payment
            const totalAllocated = (data.allocation_summary?.contract || 0) +
                (data.allocation_summary?.loans || 0) +
                (data.allocation_summary?.insurance_loans || 0);

            let contractAlloc = totalPaid;
            if (totalAllocated > 0 && totalPaid > 0) {
                const contractRatio = (data.allocation_summary?.contract || 0) / totalAllocated;
                contractAlloc = totalPaid * contractRatio;
            }

            // Determine status based on total paid vs expected
            // Status should be based on what the driver paid, not how it was allocated
            const isUpcoming = isAfter(currentWeekStart, today);
            const isCurrentWeek = isSameWeek(currentWeekStart, today, { weekStartsOn: 1 });
            const isTodayMonday = isMonday(today);
            let status: PaymentWeek['status'] = 'missed';

            if (isUpcoming) {
                status = 'upcoming';
            } else if (totalPaid >= expectedAmount) {
                // Check if payment was made on Monday (first day of the week)
                const weekMonday = currentWeekStart;
                const firstPayment = weekPayments.length > 0
                    ? weekPayments.reduce((earliest, p) =>
                        new Date(p.transaction_at) < new Date(earliest.transaction_at) ? p : earliest
                    ) : null;
                const firstPaymentDate = firstPayment ? new Date(firstPayment.transaction_at) : null;
                const paidOnMonday = firstPaymentDate && isSameDay(firstPaymentDate, weekMonday);
                status = paidOnMonday ? 'paid' : 'delayed';
            } else if (totalPaid > 0) {
                status = 'partial';
            } else if (isCurrentWeek && isTodayMonday) {
                status = 'pending';
            }

            // Calculate delay days if delayed
            let delayDays: number | null = null;
            if (status === 'delayed' && weekPayments.length > 0) {
                const weekMonday = currentWeekStart;
                const firstPayment = weekPayments.reduce((earliest, p) =>
                    new Date(p.transaction_at) < new Date(earliest.transaction_at) ? p : earliest
                );
                const firstPaymentDate = new Date(firstPayment.transaction_at);
                delayDays = Math.floor((firstPaymentDate.getTime() - weekMonday.getTime()) / (1000 * 60 * 60 * 24));
            }

            // Check for notes covering this week. period_start/period_end are DATE strings:
            // parseISO reads them on the local calendar, the one these week Mondays live on.
            const applicableNote = data.repayment_notes?.find(note => {
                const noteStart = parseISO(note.period_start);
                const noteEnd = parseISO(note.period_end);
                return currentWeekStart >= noteStart && currentWeekStart <= noteEnd;
            });

            // Skip weeks with no expected amount and no payments (pre-instalment weeks)
            if (expectedAmount === 0 && totalPaid === 0) {
                currentWeekStart = addWeeks(currentWeekStart, 1);
                continue;
            }

            weeks.push({
                weekStart: new Date(currentWeekStart),
                weekEnd: new Date(weekEnd),
                expected: expectedAmount,
                contractExpected: expectedAmount,
                payments: weekPayments,
                totalPaid,
                status,
                delayDays,
                note: applicableNote?.reason || null,
                contractAllocation: contractAlloc,
            });

            currentWeekStart = addWeeks(currentWeekStart, 1);
        }

        // Trim leading missed weeks before the first payment (chronological order)
        const firstPaymentIdx = weeks.findIndex(w => w.totalPaid > 0);
        const trimmed = firstPaymentIdx > 0 ? weeks.slice(firstPaymentIdx) : weeks;

        // Sort by most recent first
        return trimmed.reverse();
    }, [data.instalments, data.repayment_notes, data.allocation_summary, allRepayments]);

    // The merged weekly Expected (contract instalment + every active pay-now loan), keyed by
    // Monday, straight from the engine that priced the interest — so the allocation ratio below
    // always divides by the same number the interest calculation used.
    const { weeks } = useOverdueInterest(data);
    const expectedByMonday = useMemo(() => {
        const byMonday = new Map<number, number>();
        for (const week of weeks) {
            byMonday.set(week.monday.getTime(), week.expected);
        }
        return byMonday;
    }, [weeks]);

    // Apply the merged expected to the payment schedule
    const paymentScheduleWithInterest = useMemo(() => {
        if (!expectedByMonday.size || !paymentSchedule.length) {
            return paymentSchedule;
        }

        return paymentSchedule.map(week => {
            // Weeks the engine never priced (before the first payment, or past its end date) have
            // no merged figure: fall back to the contract's own instalment, which makes the ratio 1.
            const mergedExpected = expectedByMonday.get(week.weekStart.getTime()) ?? week.contractExpected;

            // Compute per-week contract allocation using the ratio of contract instalment
            // to merged instalment (which includes loans/insurance).
            // This is more accurate than the global ratio because it accounts for
            // which loans were active in each specific week.
            const contractRatio = mergedExpected > 0
                ? week.contractExpected / mergedExpected
                : 1;
            const contractPaid = week.totalPaid * contractRatio;

            return {
                ...week,
                expected: mergedExpected,  // Override with merged amount
                contractAllocation: contractPaid,  // Override with per-week ratio
            };
        });
    }, [paymentSchedule, expectedByMonday]);

    // Filter by search term
    const filteredSchedule = useMemo(() => {
        if (!debouncedSearch) return paymentScheduleWithInterest;

        const searchLower = debouncedSearch.toLowerCase();
        return paymentScheduleWithInterest.filter(week =>
            week.payments.some(p =>
                p.description?.toLowerCase().includes(searchLower) ||
                p.driver_name?.toLowerCase().includes(searchLower)
            ) ||
            week.note?.toLowerCase().includes(searchLower)
        );
    }, [paymentScheduleWithInterest, debouncedSearch]);

    // Pagination
    const totalCount = filteredSchedule.length;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const paginatedSchedule = filteredSchedule.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    // Stats for Vehicle tab
    const vehiclePrice = data.contract?.sold_at || 0;  // Total Repayable = Vehicle selling price
    // WHAT HAS GONE TO THE VEHICLE: the deposit the driver put down, plus the vehicle's share of
    // every payment since. The deposit is part of the Price the agreement sets out and the Summary
    // tab has always counted it as paid — see `vehicleRepayment` for why this tab now does too,
    // and for what stays out of it. [D-B58]
    const { repaid: vehicleRepaidTotal, outstanding: vehicleOutstanding } = vehicleRepayment(
        data.contract?.sold_at,
        data.allocation_summary?.contract,
        data.deposit,
    );
    const managementRate = data.contract?.management_rate ?? null;
    const hasMgmtSplit = managementRate !== null;

    // Reset to 'all' if management_rate is cleared
    const activeMode = hasMgmtSplit ? activeView : 'all';

    // Multiplier for current mode's portion of the allocation
    const modeMultiplier = activeMode === 'management' && managementRate !== null
        ? managementRate / 100
        : activeMode === 'partner' && managementRate !== null
            ? (100 - managementRate) / 100
            : 1;

    // REPAID AND OUTSTANDING ARE NOT SCALED BY THE MULTIPLIER ABOVE, and the reason is the
    // deposit inside them: no partner has ever been paid a share of a contract deposit, so a
    // share of one has no meaning in a card labelled "Payout" or "Retained". There is nowhere to
    // put one here in any case — `activeView` above is pinned to 'all' with no setter, so this
    // tab has no share views at all, which is why the Outstanding description below is not
    // branched either. Anyone who gives this tab a toggle has to drop the deposit outside the
    // 'all' view first, the way fleet-admin's `vehicleRepaid` does, and revisit these labels with
    // it. [D-B58]

    // Latest contract instalment
    const latestInstalment = data.instalments && data.instalments.length > 0
        ? [...data.instalments].sort((a, b) =>
            new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime()
        )[0]?.amount || 0
        : 0;

    // Average of contract allocations per week (missed weeks count as 0)
    // Multiple payments in a week are already summed in contractAllocation
    const totalWeeks = paymentSchedule.length;
    const totalContractAllocations = paymentSchedule.reduce((sum, w) => sum + w.contractAllocation, 0);
    const avgContractPayment = totalWeeks > 0 ? (totalContractAllocations * modeMultiplier) / totalWeeks : 0;

    // Card labels based on mode
    const repaidLabel = activeMode === 'management' ? 'Retained' : activeMode === 'partner' ? 'Payout' : 'Repaid';
    const repaidDescription = activeMode === 'management'
        ? `Total management fee retained (${managementRate}% of vehicle allocation).`
        : activeMode === 'partner'
            ? `Total partner payout portion (${managementRate !== null ? 100 - managementRate : 100}% of vehicle allocation).`
            : 'The deposit, plus the share of every payment that has gone towards the vehicle.';
    const totalRepayableDescription = activeMode === 'partner'
        ? `Partner's portion of the vehicle selling price.`
        : activeMode === 'management'
            ? `Management's portion of the vehicle selling price.`
            : 'The vehicle selling price — what the deposit and the weekly instalments together repay.';




    const getStatusBadge = (status: PaymentWeek['status'], delayDays?: number | null) => {
        switch (status) {
            case 'paid':
                return (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <Check className="h-3 w-3 mr-1" />
                        Paid
                    </Badge>
                );
            case 'partial':
                return (
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Partial
                    </Badge>
                );
            case 'missed':
                return (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Missed
                    </Badge>
                );
            case 'delayed':
                return (
                    <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                        <Clock className="h-3 w-3 mr-1" />
                        Delayed {delayDays ? `(${delayDays}d)` : ''}
                    </Badge>
                );
            case 'upcoming':
                return (
                    <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">
                        Upcoming
                    </Badge>
                );
            case 'pending':
                return (
                    <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                        Pending
                    </Badge>
                );
        }
    };

    return (
        <div className="space-y-6">


            <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
                <StatCard
                    title="Total Repayable"
                    value={<CurrencyDisplay amount={vehiclePrice * modeMultiplier} />}
                    description={totalRepayableDescription}
                />
                <StatCard
                    title={repaidLabel}
                    value={<CurrencyDisplay amount={vehicleRepaidTotal} />}
                    description={repaidDescription}
                />
                <StatCard
                    title="Outstanding"
                    value={<CurrencyDisplay amount={vehicleOutstanding} />}
                    description="What is left of the vehicle price after the deposit and the payments so far."
                />
                <StatCard
                    title="Instalment"
                    value={<CurrencyDisplay amount={latestInstalment * modeMultiplier} />}
                    description="The current weekly instalment amount expected for vehicle repayment."
                />
                <StatCard
                    title="Average Payment"
                    value={<CurrencyDisplay amount={avgContractPayment} />}
                    description={`Average weekly allocation across ${totalWeeks} weeks. Missed weeks count as zero; multiple payments in a week are summed together. A deposit is not a weekly payment, so it is not counted here.`}
                />
            </div>

            {/* Payment Schedule Table */}
            <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by description..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {totalPages > 0 && (
                        <ListPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                        />
                    )}
                </div>

                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : paginatedSchedule.length > 0 ? (
                    <>
                        {/* Mobile: Card layout */}
                        <div className="lg:hidden space-y-3">
                            {paginatedSchedule.map((week) => {
                                const weekKey = week.weekStart.toISOString();
                                const isExpanded = expandedWeeks.has(weekKey);
                                const hasMultiplePayments = week.payments.length > 1;

                                return (
                                    <div
                                        key={weekKey}
                                        className={cn(
                                            "p-4 rounded-xl border border-border/40 bg-card flex flex-col gap-3 shadow-sm transition-all",
                                            hasMultiplePayments && "cursor-pointer hover:border-primary/30"
                                        )}
                                        onClick={() => hasMultiplePayments && toggleWeekExpanded(weekKey)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex flex-col gap-1.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-sm">
                                                        {format(week.weekStart, 'MMM d')} - {format(week.weekEnd, 'MMM d, yy')}
                                                    </span>
                                                    {hasMultiplePayments && (
                                                        <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                                            {week.payments.length} PMTs
                                                            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    {getStatusBadge(week.status, week.delayDays)}
                                                    {week.note && (
                                                        <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                                                            • {week.note}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 text-right">
                                                <div className="text-xs text-muted-foreground">
                                                    Expected <span className="font-medium text-foreground">{formatCurrency(week.contractExpected * modeMultiplier)}</span>
                                                </div>
                                                <div className={cn(
                                                    "font-bold text-base",
                                                    week.status === 'paid' && "text-green-600",
                                                    week.status === 'partial' && "text-yellow-600",
                                                    week.status === 'missed' && "text-red-600"
                                                )}>
                                                    {formatCurrency(week.contractAllocation * modeMultiplier)}
                                                </div>
                                            </div>
                                        </div>

                                        {hasMultiplePayments && isExpanded && (
                                            <div className="mt-2 pt-3 border-t border-border/50 flex flex-col gap-2">
                                                {week.payments.map((payment) => {
                                                    const weekContractRatio = week.totalPaid > 0 ? week.contractAllocation / week.totalPaid : 1;
                                                    const paymentContractAlloc = payment.amount * weekContractRatio;
                                                    return (
                                                        <div key={payment.id} className="flex items-center justify-between text-sm pl-2 border-l-2 border-muted">
                                                            <div className="flex flex-col">
                                                                <span className="text-muted-foreground text-xs">
                                                                    {format(new Date(payment.transaction_at), 'MMM d, h:mm a')}
                                                                </span>
                                                                <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                                                                    {payment.description || '-'}
                                                                </span>
                                                            </div>
                                                            <span className="font-medium">
                                                                {formatCurrency(paymentContractAlloc * modeMultiplier)}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Desktop: Table layout */}
                        <div className="hidden lg:block">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Week</TableHead>
                                        <TableHead>Expected</TableHead>
                                        <TableHead>Paid</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="w-[200px]">Note</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedSchedule.map((week) => {
                                        const weekKey = week.weekStart.toISOString();
                                        const isExpanded = expandedWeeks.has(weekKey);
                                        const hasMultiplePayments = week.payments.length > 1;

                                        return (
                                            <Fragment key={weekKey}>
                                                <TableRow
                                                    className={cn(hasMultiplePayments && "cursor-pointer hover:bg-muted/50")}
                                                    onClick={() => hasMultiplePayments && toggleWeekExpanded(weekKey)}
                                                >
                                                    <TableCell className="text-muted-foreground whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            {hasMultiplePayments && (
                                                                isExpanded
                                                                    ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                                    : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                            )}
                                                            {format(week.weekStart, 'MMM d')} - {format(week.weekEnd, 'MMM d, yyyy')}
                                                            {hasMultiplePayments && (
                                                                <span className="text-xs text-muted-foreground">({week.payments.length})</span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {formatCurrency(week.contractExpected * modeMultiplier)}
                                                    </TableCell>
                                                    <TableCell className={cn(
                                                        "font-medium",
                                                        week.status === 'paid' && "text-green-600",
                                                        week.status === 'partial' && "text-yellow-600",
                                                        week.status === 'missed' && "text-red-600"
                                                    )}>
                                                        {formatCurrency(week.contractAllocation * modeMultiplier)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {getStatusBadge(week.status, week.delayDays)}
                                                    </TableCell>
                                                    <TableCell className="max-w-[150px] truncate text-muted-foreground">
                                                        {week.note || '-'}
                                                    </TableCell>
                                                </TableRow>
                                                {hasMultiplePayments && isExpanded && week.payments.map((payment) => {
                                                    const weekContractRatio = week.totalPaid > 0 ? week.contractAllocation / week.totalPaid : 1;
                                                    const paymentContractAlloc = payment.amount * weekContractRatio;
                                                    return (
                                                        <TableRow key={payment.id} className="bg-muted/30 border-0">
                                                            <TableCell className="pl-10 text-sm text-muted-foreground">
                                                                ↳ {format(new Date(payment.transaction_at), 'MMM d, h:mm a')}
                                                            </TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell className="text-sm text-muted-foreground">
                                                                {formatCurrency(paymentContractAlloc * modeMultiplier)}
                                                            </TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                                                                {payment.description || '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-xl bg-muted/20">
                        <AlertTriangle className="h-8 w-8 mx-auto mb-3 opacity-30" />
                        <p className="text-sm font-medium">No payment records</p>
                    </div>
                )}
            </div>
        </div>
    );
}
